import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, TextField, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Tooltip, Chip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'
import StatusBadge from '../../../components/ui/StatusBadge'

Index.layout = (page) => <AppShell>{page}</AppShell>

const CATEGORY_LABELS = {
  weg: 'WEG', mv: 'MV', sev: 'SEV', privat: 'Privat',
}

export default function Index({ properties, filters = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const [q, setQ] = useState(filters.q || '')
  const [managementType, setManagementType] = useState(filters.management_type || '')
  const [active, setActive] = useState(filters.active || '')

  const applyFilters = () => {
    router.get(`/m/${tenant.slug}/properties`, {
      q: q || undefined,
      management_type: managementType || undefined,
      active: active || undefined,
    }, { preserveState: true, replace: true })
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') applyFilters()
  }

  return (
    <Box>
      <PageHeader
        title="Immobilien"
        breadcrumbs={[{ label: 'Dashboard', href: `/m/${tenant.slug}` }, { label: 'Immobilien' }]}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => router.visit(`/m/${tenant.slug}/properties/new`)}
            data-testid="new-property-btn"
          >
            Neue Immobilie
          </Button>
        }
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Suche nach Name, Referenz..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          sx={{ minWidth: 240 }}
          data-testid="search-input"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Verwaltungsart</InputLabel>
          <Select
            value={managementType}
            label="Verwaltungsart"
            onChange={(e) => setManagementType(e.target.value)}
            data-testid="management-type-filter"
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="weg">WEG</MenuItem>
            <MenuItem value="mv">MV</MenuItem>
            <MenuItem value="sev">SEV</MenuItem>
            <MenuItem value="privat">Privat</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={active}
            label="Status"
            onChange={(e) => setActive(e.target.value)}
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="true">Aktiv</MenuItem>
            <MenuItem value="false">Inaktiv</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={applyFilters}>
          Filtern
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Referenz</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Art</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Einheiten</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Verwaltung seit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {properties.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Immobilien gefunden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {properties.map((property) => (
              <TableRow
                key={property.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}`)}
                data-testid={`property-row-${property.id}`}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{property.reference}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{property.name}</Typography>
                  {property.address_one_line && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {property.address_one_line}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge type="management_type" value={property.management_type} />
                </TableCell>
                <TableCell>{property.unit_count}</TableCell>
                <TableCell>{property.management_start}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={property.active ? 'Aktiv' : 'Inaktiv'}
                    color={property.active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Bearbeiten">
                    <IconButton
                      size="small"
                      onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/edit`)}
                      data-testid={`edit-property-${property.id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
